/**
 * @rogueworlds/client — scene, camera, sky, fog and the single shadow-casting sun.
 *
 * Lighting/shadow budget (convention 5): ONE shadow-casting directional light, PCF soft shadows, an
 * explicitly sized orthographic frustum that follows the player, and a sky/fog pair that shares one
 * colour at the horizon so the streaming boundary of the loaded chunks is hidden.
 *
 * Shadow map: 2048 x 2048. `followSun` keeps an orthographic frustum of `2 * extent` metres centred
 * on the player, where `extent = clamp(viewDistanceM, 16, 64)`. At the default 32 m view distance
 * (10 chunks of 3.2 m) that is a 64 m span, i.e. 64 / 2048 = 3.1 cm per shadow texel — about three
 * texels per 10 cm voxel, which is the density voxel-scale shadows need. Dropping the same map to
 * 1024 on mobile would double that to 6.3 cm/texel, i.e. roughly one texel per voxel.
 *
 * The sky is a vertex-coloured inverted sphere that follows the camera (see `onBeforeRender`): it
 * inherits no camera rotation, so the horizon stays level, and it costs zero per-frame work from
 * the render loop. Its horizon colour IS the fog colour.
 */

import * as THREE from "three";

/** Sky/horizon/ground tints. The horizon tint is reused verbatim as the fog colour. */
const SKY_ZENITH = 0x4f7ec4;
const SKY_HORIZON = 0xb6cbe0;
const SKY_GROUND = 0x6f7681;
const FOG_COLOR = SKY_HORIZON;
const SUN_COLOR = 0xfff0d6;
const HEMISPHERE_SKY = 0xbfd8f2;
const HEMISPHERE_GROUND = 0x51483a;

/**
 * Fog range defaults, driven by the view distance the client actually streams. Chunks stream in to
 * `viewDistanceM`, so fog must be fully opaque slightly before that to hide the boundary.
 */
const DEFAULT_VIEW_DISTANCE_M = 32;
const FOG_NEAR_FACTOR = 0.6;
const FOG_FAR_FACTOR = 2;

const CAMERA_FOV_DEG = 72;
/** 5 cm: close enough that a 10 cm voxel never clips when the camera is against a wall. */
const CAMERA_NEAR_M = 0.05;
/** Must clear the sky dome (200 m) at every supported view distance. */
const CAMERA_FAR_M = 512;

/** Fixed sun direction. `followSun` keeps this direction and moves only the light's position. */
const SUN_DIRECTION = new THREE.Vector3(0.55, 0.82, 0.3).normalize();
const SUN_SHADOW_MAP_SIZE = 2048;
const SUN_SHADOW_BIAS = -0.0006;
/** 2 cm of normal bias: on the order of a fifth of a voxel, so acne stays off flat terrain. */
const SUN_SHADOW_NORMAL_BIAS = 0.02;
const SHADOW_EXTENT_MIN_M = 16;
const SHADOW_EXTENT_MAX_M = 64;
/** Light standoff as a multiple of the frustum half-extent (see `followSun` for the depth range). */
const SHADOW_DISTANCE_FACTOR = 3;

/** Radius of the sky dome. Large enough to sit behind every fogged chunk at any view distance. */
const SKY_RADIUS_M = 200;

export interface SceneHandles {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly sun: THREE.DirectionalLight;
  readonly hemisphere: THREE.HemisphereLight;
}

/** Sky dome per scene, so `disposeScene` can free exactly what `createScene` created. */
type SkyDome = THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>;
const skyDomes = new WeakMap<THREE.Scene, SkyDome>();

/** Scratch vectors: `followSun` runs every frame and must not allocate. */
const scratchDirection = new THREE.Vector3();
const scratchOrigin = new THREE.Vector3();

/** Builds the vertex-coloured sky dome. Colours run ground -> horizon -> zenith by vertex height. */
function createSkyDome(): SkyDome {
  const geometry = new THREE.SphereGeometry(SKY_RADIUS_M, 24, 16);
  const position = geometry.getAttribute("position");
  const colors = new Float32Array(position.count * 3);
  const zenith = new THREE.Color(SKY_ZENITH);
  const horizon = new THREE.Color(SKY_HORIZON);
  const ground = new THREE.Color(SKY_GROUND);
  const tint = new THREE.Color();

  for (let vertex = 0; vertex < position.count; vertex++) {
    const height = Math.min(1, Math.max(-1, (position.getY(vertex) ?? 0) / SKY_RADIUS_M));
    if (height >= 0) {
      // Quadratic falloff: the horizon band stays tight and the zenith holds its colour.
      tint.copy(horizon).lerp(zenith, height * height);
    } else {
      tint.copy(horizon).lerp(ground, height * height);
    }
    colors[vertex * 3] = tint.r;
    colors[vertex * 3 + 1] = tint.g;
    colors[vertex * 3 + 2] = tint.b;
  }

  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const material = new THREE.MeshBasicMaterial({
    vertexColors: true,
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
  });

  const sky = new THREE.Mesh(geometry, material);
  // Follow the camera without inheriting its rotation. three.js recomputes `modelViewMatrix` after
  // this hook, so the moved dome is used in the same frame; nothing is allocated here.
  sky.onBeforeRender = (_renderer, _scene, camera): void => {
    sky.position.copy(camera.position);
    sky.updateMatrixWorld();
  };
  // Drawn last among the opaque pass: terrain has already written depth, so early-z discards every
  // sky fragment a chunk covers instead of shading the whole screen behind the world.
  sky.renderOrder = 1;
  sky.name = "rw-sky";
  return sky;
}

/** Creates the scene, camera, sky, fog and lights. `aspect` is width / height in CSS pixels. */
export function createScene(aspect: number): SceneHandles {
  const scene = new THREE.Scene();
  scene.fog = new THREE.Fog(
    FOG_COLOR,
    DEFAULT_VIEW_DISTANCE_M * FOG_NEAR_FACTOR,
    DEFAULT_VIEW_DISTANCE_M * FOG_FAR_FACTOR,
  );

  const camera = new THREE.PerspectiveCamera(CAMERA_FOV_DEG, aspect, CAMERA_NEAR_M, CAMERA_FAR_M);
  // The camera is in the scene graph so its own children (none today) and the shadow-driven
  // helpers keep sane world matrices.
  scene.add(camera);

  const sky = createSkyDome();
  scene.add(sky);
  skyDomes.set(scene, sky);

  const hemisphere = new THREE.HemisphereLight(HEMISPHERE_SKY, HEMISPHERE_GROUND, 1);
  scene.add(hemisphere);

  const sun = new THREE.DirectionalLight(SUN_COLOR, 2.6);
  sun.position.copy(SUN_DIRECTION).multiplyScalar(SHADOW_EXTENT_MAX_M * SHADOW_DISTANCE_FACTOR);
  sun.castShadow = true;
  sun.shadow.mapSize.set(SUN_SHADOW_MAP_SIZE, SUN_SHADOW_MAP_SIZE);
  sun.shadow.bias = SUN_SHADOW_BIAS;
  sun.shadow.normalBias = SUN_SHADOW_NORMAL_BIAS;
  scene.add(sun);
  // The target must be in the graph for its world matrix to be refreshed each frame.
  scene.add(sun.target);
  followSun(sun, scratchOrigin.set(0, 0, 0), DEFAULT_VIEW_DISTANCE_M);

  return { scene, camera, sun, hemisphere };
}

/**
 * Centres the sun's shadow frustum on `target` (the player) and sizes it from the view distance.
 *
 * The light is placed `3 * extent` along its current direction from the target, which bounds the
 * depth of everything inside the frustum to `3 * extent ± sqrt(3) * extent`; the near/far planes
 * are set to `1 * extent` / `5 * extent` so nothing inside the box is clipped. Allocation-free.
 */
export function followSun(shadows: THREE.DirectionalLight, target: THREE.Vector3, viewDistanceM: number): void {
  const extent = Math.min(SHADOW_EXTENT_MAX_M, Math.max(SHADOW_EXTENT_MIN_M, viewDistanceM));
  const distance = extent * SHADOW_DISTANCE_FACTOR;
  const shadowCamera = shadows.shadow.camera;

  // Keep the sun's direction; only its position follows. Falls back to the authored direction if
  // the light was never placed relative to its target.
  scratchDirection.copy(shadows.position).sub(shadows.target.position);
  if (scratchDirection.lengthSq() < 1e-8) {
    scratchDirection.copy(SUN_DIRECTION);
  } else {
    scratchDirection.normalize();
  }

  shadows.target.position.copy(target);
  shadows.position.copy(target).addScaledVector(scratchDirection, distance);
  shadows.target.updateMatrixWorld();

  shadowCamera.left = -extent;
  shadowCamera.right = extent;
  shadowCamera.top = extent;
  shadowCamera.bottom = -extent;
  shadowCamera.near = extent;
  shadowCamera.far = extent * 5;
  shadowCamera.updateProjectionMatrix();
}

/**
 * Retunes the fog range. Additive convenience: it narrows the `Fog | FogExp2 | null` union that the
 * scene exposes so callers do not have to cast.
 */
export function setFogRange(scene: THREE.Scene, nearM: number, farM: number): void {
  const fog = scene.fog;
  if (fog !== null && fog instanceof THREE.Fog) {
    fog.near = nearM;
    fog.far = farM;
    return;
  }
  scene.fog = new THREE.Fog(FOG_COLOR, nearM, farM);
}

/** Frees everything `createScene` allocated. Additive export; the rest of the scene is caller-owned. */
export function disposeScene(handles: SceneHandles): void {
  const sky = skyDomes.get(handles.scene);
  if (sky !== undefined) {
    handles.scene.remove(sky);
    sky.geometry.dispose();
    sky.material.dispose();
    skyDomes.delete(handles.scene);
  }
  handles.scene.fog = null;
}
